import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Divider,
  Avatar,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  Button,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  VideoCall as VideoCallIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/Layout";

const MyBookings = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  // Sample bookings data
  const bookings = [
    {
      id: 1,
      service: "Tax Consultation",
      consultant: "Sarah Johnson",
      date: "2025-10-08",
      time: "10:00 AM",
      duration: "60 mins",
      status: "upcoming",
      type: "video",
      avatar: "SJ",
    },
    {
      id: 2,
      service: "Financial Planning",
      consultant: "Michael Chen",
      date: "2025-10-12",
      time: "2:00 PM",
      duration: "45 mins",
      status: "upcoming",
      type: "video",
      avatar: "MC",
    },
    {
      id: 3,
      service: "Bookkeeping Review",
      consultant: "Emma Davis",
      date: "2025-09-28",
      time: "11:00 AM",
      duration: "30 mins",
      status: "completed",
      type: "video",
      avatar: "ED",
    },
    {
      id: 4,
      service: "Audit Preparation",
      consultant: "David Martinez",
      date: "2025-09-15",
      time: "3:30 PM",
      duration: "90 mins",
      status: "completed",
      type: "video",
      avatar: "DM",
    },
    {
      id: 5,
      service: "VAT Returns Assistance",
      consultant: "Lisa Anderson",
      date: "2025-09-20",
      time: "9:00 AM",
      duration: "45 mins",
      status: "cancelled",
      type: "video",
      avatar: "LA",
    },
  ];

  const handleMenuOpen = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "upcoming":
        return "primary";
      case "completed":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "upcoming":
        return <ScheduleIcon fontSize="small" />;
      case "completed":
        return <CheckCircleIcon fontSize="small" />;
      case "cancelled":
        return <CancelIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.service.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.consultant.toLowerCase().includes(searchQuery.toLowerCase());

    if (tabValue === 0) return matchesSearch;
    if (tabValue === 1) return matchesSearch && booking.status === "upcoming";
    if (tabValue === 2) return matchesSearch && booking.status === "completed";
    if (tabValue === 3) return matchesSearch && booking.status === "cancelled";
    return matchesSearch;
  });

  return (
    <Layout>
      <Box sx={{ bgcolor: "#f5f7fa", minHeight: "100vh", py: 4 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight="700" gutterBottom>
              My Bookings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and track your accounting consultation appointments
            </Typography>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search bookings by service or consultant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: "white", borderRadius: 2 }}
            />
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="All Bookings" />
              <Tab label="Upcoming" />
              <Tab label="Completed" />
              <Tab label="Cancelled" />
            </Tabs>
          </Box>

          {/* Bookings List */}
          <Grid container spacing={3}>
            {filteredBookings.map((booking) => (
              <Grid item xs={12} key={booking.id}>
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            bgcolor: "primary.main",
                            fontSize: "1.2rem",
                            fontWeight: "600",
                          }}
                        >
                          {booking.avatar}
                        </Avatar>

                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="h6" fontWeight="600">
                              {booking.service}
                            </Typography>
                            <Chip
                              label={booking.status}
                              color={getStatusColor(booking.status)}
                              size="small"
                              icon={getStatusIcon(booking.status)}
                              sx={{ textTransform: "capitalize" }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mb: 1,
                            }}
                          >
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {booking.consultant}
                            </Typography>
                          </Box>

                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={4}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CalendarIcon
                                  fontSize="small"
                                  color="primary"
                                />
                                <Typography variant="body2" fontWeight="500">
                                  {new Date(booking.date).toLocaleDateString(
                                    "en-US",
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    }
                                  )}
                                </Typography>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <TimeIcon fontSize="small" color="primary" />
                                <Typography variant="body2" fontWeight="500">
                                  {booking.time}
                                </Typography>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <VideoCallIcon
                                  fontSize="small"
                                  color="primary"
                                />
                                <Typography variant="body2" fontWeight="500">
                                  {booking.duration}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {booking.status === "upcoming" && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VideoCallIcon />}
                                sx={{ mr: 1 }}
                              >
                                Join Meeting
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EventIcon />}
                              >
                                Reschedule
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <IconButton
                        onClick={(e) => handleMenuOpen(e, booking)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredBookings.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <AccountBalanceIcon
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "You don't have any bookings yet"}
              </Typography>
            </Box>
          )}

          {/* Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
            <MenuItem onClick={handleMenuClose}>Download Invoice</MenuItem>
            {selectedBooking?.status === "upcoming" && (
              <>
                <MenuItem onClick={handleMenuClose}>Reschedule</MenuItem>
                <Divider />
                <MenuItem
                  onClick={handleMenuClose}
                  sx={{ color: "error.main" }}
                >
                  Cancel Booking
                </MenuItem>
              </>
            )}
          </Menu>
        </Container>
      </Box>
    </Layout>
  );
};

export default MyBookings;
